import type { DrawingEntity, Vec3 } from '../types';
import { lineEndpoints, linePlanPoints } from './line3d';

/** A parallel copy of a spatial line in the floor plane. Positive is left of start→end. */
export function offsetLine(drawing: DrawingEntity, distanceMm: number): [Vec3, Vec3] | null {
  if (drawing.kind !== 'line' || !Number.isFinite(distanceMm) || distanceMm === 0) return null;
  const [start, end] = lineEndpoints(drawing);
  const dx = end[0] - start[0], dz = end[2] - start[2];
  const length = Math.hypot(dx, dz);
  if (length < .001) return null;
  const x = -dz / length * distanceMm, z = dx / length * distanceMm;
  return [[start[0] + x, start[1], start[2] + z], [end[0] + x, end[1], end[2] + z]];
}

/** Mirror around the selected drawing's own centre, without moving its bounding box. */
export function mirrorDrawing(drawing: DrawingEntity, axis: 'x' | 'z'): DrawingEntity {
  const coordinates = drawing.vertices3d ? drawing.vertices3d.map((point) => [point[0], point[2]]) : drawing.points3d ? drawing.points3d.map((point) => [point[0], point[2]]) : drawing.points;
  const pivot = (Math.min(...coordinates.map((point) => point[axis === 'x' ? 0 : 1])) + Math.max(...coordinates.map((point) => point[axis === 'x' ? 0 : 1]))) / 2;
  const reflect = (point: [number, number]): [number, number] => axis === 'x' ? [2 * pivot - point[0], point[1]] : [point[0], 2 * pivot - point[1]];
  const points3d = drawing.points3d?.map((point) => {
    const [x, z] = reflect([point[0], point[2]]);
    return [x, point[1], z] as Vec3;
  }) as [Vec3, Vec3] | undefined;
  const vertices3d = drawing.vertices3d?.map((point) => {
    const [x, z] = reflect([point[0], point[2]]);
    return [x, point[1], z] as Vec3;
  });
  return { ...drawing, points: vertices3d ? vertices3d.map(([x, , z]) => [x, z]) : points3d ? linePlanPoints(points3d) : drawing.points.map(reflect), points3d, vertices3d };
}
